import './MyCourses.scss';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CommunicationMessage, Illustrations, IllustrationSize } from '../../Communication';
import { ThemeMode, useProviderTheme } from '../../hooks/useProviderTheme';
import { Search } from '../../Search/Search';
import { MyCoursesColumns, MyCoursesEntry, MyCoursesProps } from './MyCourses.interface';
import { MyCoursesListEntry } from './MyCoursesListEntry';

export const MyCourses: React.FC<MyCoursesProps> = ({
  courses,
  onCourseClick,
  columns,
  showHeader,
}) => {
  const { mode } = useProviderTheme();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const applyFilters = (courses: MyCoursesEntry[], searchTerm: string): MyCoursesEntry[] => {
    let filtered = courses;

    if (searchTerm.trim() !== '') {
      filtered = filtered.filter((course) =>
        course.courseName.toLowerCase().includes(searchTerm.trim().toLocaleLowerCase()),
      );
    }

    return filtered;
  };

  const coursesFiltered = applyFilters(courses, searchTerm);

  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  return (
    <div className="myCourses">
      {courses.length === 0 && (
        <div className="flex emptyMsgWrapper">
          <CommunicationMessage
            illustration={Illustrations.Empty}
            headline={t('classbook:my_courses:no_courses', {
              defaultValue: 'Keine Kurse vorhanden',
            })}
            text={t('classbook:my_courses:currenty_no_courses_you_are_teaching', {
              defaultValue: 'Zur Zeit sind Ihnen keine Kurse zugewiesen',
            })}
            useDarkIllustration={mode === ThemeMode.Dark}
            illustrationSize={IllustrationSize.Small}
          />
        </div>
      )}
      {courses.length > 0 && (
        <>
          {showHeader && (
            <div className="flex coursesHeader">
              <div className="col colCourseIcon"></div>
              {(columns & MyCoursesColumns.CourseName) === MyCoursesColumns.CourseName && (
                <div className="col colCourseName">{t('course', { defaultValue: 'Kurs' })}</div>
              )}

              {(columns & MyCoursesColumns.ClassGrade) === MyCoursesColumns.ClassGrade && (
                <div className="col colClassGradeName">
                  {t('classbook:my_courses:grade/class', {
                    defaultValue: 'Stufe / Klasse',
                  })}
                </div>
              )}

              {(columns & MyCoursesColumns.NextOccurrence) ===
                MyCoursesColumns.NextOccurrence && (
                <div className="col colNextTeachingUnit">
                  {t('classbook:my_courses:next_teaching_unit', {
                    defaultValue: 'Nächste Unterrichtseinheit',
                  })}
                </div>
              )}

              <div className="action"></div>
            </div>
          )}
          <div className="flex flex-col gap-2 mb-1">
            <Search handleSearchInputChange={handleSearchTermChange} searchSuggestions={[]} />
          </div>
          <ul className="coursesList" aria-label={t('courses', { defaultValue: 'Kurse' })}>
            {coursesFiltered.map((course) => (
              <MyCoursesListEntry
                aria-label={`${t('classbook:mycourses:navigate_to_course_label', {
                  defaultValue: 'Öffnet Unterseite folgender Kurs: {{courseName}}',
                  courseName: course.courseName,
                })}`}
                key={course.id}
                course={course}
                onClick={onCourseClick.bind(this, course)}
                columns={columns}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
